/**
 * ACC-031: clinics with access to this customer's records, and a control to revoke it.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { ClinicAccess, ClinicAccessList, ClinicShareScope } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { ConfirmDialog } from "@lattice/ui/ConfirmDialog";
import { pageHeading } from "@lattice/ui/pageHeading";
import { Button } from "@lattice/ui/Button";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { loadClinics, revokeClinic, setClinicShare } from "../api.ts";
import { AccountShell } from "./Shell.tsx";
const DAY = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC"
});
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "signed_out";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "list";
  readonly clinics: ClinicAccessList;
};
function day(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : DAY.format(date);
}
function scopeLine(clinic: ClinicAccess): string {
  const what = clinic.shareScope === "all" ? "all your records" : "records they created";
  const when = day(clinic.createdAt);
  if (clinic.status === "pending") return `Asked ${when} to see ${what}.`;
  return `Can see ${what}. Linked ${when}.`;
}
function nextScope(scope: ClinicShareScope): ClinicShareScope {
  return scope === "all" ? "clinic_created" : "all";
}
function ClinicRow({
  clinic,
  busy,
  onRevoke,
  onShare
}: {
  readonly clinic: ClinicAccess;
  readonly busy: boolean;
  readonly onRevoke: () => void;
  readonly onShare: () => void;
}) {
  return <li data-testid={`clinic-${clinic.id}`} className="xrvj5dj xju1xpo x1vdzsm xcfm6fe x6s0dn4 x1wiri7k xez8t42 x13fuv20 x178xt8z">
      <div>
        <p className="x1ghz6dp xqu410z">{clinic.name}</p>
        <p className="x1ghz6dp xl4dl4e x1jty25b">{scopeLine(clinic)}</p>
      </div>
      <div className="x78zum5 xdt5ytf x1173whf xuk3077">
        <Button tone="quiet" size="small" busy={busy} testId={`share-${clinic.id}`} onClick={onShare}>
          {clinic.shareScope === "all" ? "Share only records they created" : "Share all records"}
        </Button>
        <Button variant="dangerSecondary" size="md" busy={busy} testId={`revoke-${clinic.id}`} onClick={onRevoke}>
          {clinic.status === "pending" ? "Decline request" : "Revoke access"}
        </Button>
      </div>
    </li>;
}
export function AccountClinics() {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ClinicAccess | null>(null);
  const [widening, setWidening] = useState<ClinicAccess | null>(null);
  const load = () => {
    setScreen({
      kind: "loading"
    });
    void loadClinics().then(loaded => {
      if (loaded === "signed_out") setScreen({
        kind: "signed_out"
      });else if (loaded === null) setScreen({
        kind: "failed"
      });else setScreen({
        kind: "list",
        clinics: loaded
      });
    }, () => setScreen({
      kind: "failed"
    }));
  };
  useEffect(() => {
    document.title = "Who can see your records";
    load();
  }, []);
  if (screen.kind === "loading") return <AccountShell title="Who can see your records">
        <Pending message="Loading clinics" bones={["title", "line", "row"]} />
      </AccountShell>;
  if (screen.kind === "signed_out") return <AccountShell title="Who can see your records" signedOut>
        {null}
      </AccountShell>;
  if (screen.kind === "failed") return <AccountShell title="Who can see your records">
        <p className="x1ghz6dp x1jty25b x1fzhlzt">
          Try again in a minute. If it keeps happening, write to support.
        </p>
        <Button onClick={load}>Try again</Button>
      </AccountShell>;
  const revoke = async (id: string, name: string): Promise<string | null> => {
    const next = await revokeClinic(id);
    if (next === null) return "We couldn't revoke that. Try again.";
    setScreen({
      kind: "list",
      clinics: next
    });
    setNote(`${name} can no longer see your records.`);
    return null;
  };

  /** The problem to show, or null once the clinic's reach has changed and the page says so. */
  const share = async (clinic: ClinicAccess): Promise<string | null> => {
    setBusyId(clinic.id);
    setProblem(null);
    setNote(null);
    const to = nextScope(clinic.shareScope);
    const next = await setClinicShare(clinic.id, to).catch(() => null);
    setBusyId(null);
    if (next === null) return `We couldn't change what ${clinic.name} can see. Nothing changed. Try again.`;
    setScreen({
      kind: "list",
      clinics: next
    });
    setNote(to === "all" ? `${clinic.name} can now see all your records.` : `${clinic.name} can now see only the records they created.`);
    return null;
  };

  /* Narrowing what a clinic sees needs no question. Widening does. */
  const choose = (clinic: ClinicAccess) => {
    if (clinic.shareScope === "all") {
      void share(clinic).then(problem => {
        if (problem !== null) setProblem(problem);
      });
    } else setWidening(clinic);
  };
  return <AccountShell title="Who can see your records">
      <p className="x1ghz6dp x1jty25b x1fzhlzt">
        These clinics can open your labs and formula. You can revoke access any time.
      </p>
      {screen.clinics.clinics.length === 0 ? <p className="x1ghz6dp x1jty25b x1fzhlzt" data-testid="no-clinics">
          No clinic has access to your records.
        </p> : <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" data-testid="clinic-list">
          {screen.clinics.clinics.map(clinic => <ClinicRow key={clinic.id} clinic={clinic} busy={busyId === clinic.id} onRevoke={() => setRevoking(clinic)} onShare={() => choose(clinic)} />)}
        </ul>}
      {/* Below the list, so a result appearing never pushes the row that was just pressed. */}
      {problem !== null && <Status state="problem">{problem}</Status>}
      {note === null ? null : <Status state="ok">{note}</Status>}
      <ConfirmDialog open={widening !== null} onClose={() => setWidening(null)} title={`Let ${widening?.name ?? "this clinic"} see all your records?`} cancelFirst confirmLabel="Share all records" cancelLabel="Keep sharing less" testId="widen-clinic" onConfirm={() => widening === null ? Promise.resolve(null) : share(widening)}>
        {widening?.name ?? "This clinic"} sees only the records it created. After this it can open
        all your labs and formulas, including ones made by you or by other clinics. You can go back
        to sharing less at any time.
      </ConfirmDialog>
      <ConfirmDialog open={revoking !== null} onClose={() => setRevoking(null)} title={revoking?.status === "pending" ? `Decline ${revoking.name}'s request?` : `Stop ${revoking?.name ?? "this clinic"} seeing your records?`} tone="danger" confirmLabel={revoking?.status === "pending" ? "Decline request" : "Revoke access"} cancelLabel={revoking?.status === "pending" ? "Keep request" : "Keep access"} testId="revoke-clinic" fallbackFocus={pageHeading} onConfirm={() => revoking === null ? Promise.resolve(null) : revoke(revoking.id, revoking.name)}>
        They can no longer open your labs or formula. Your own account, formula and subscription are
        not touched.
      </ConfirmDialog>
    </AccountShell>;
}